if (!customElements.get('newsletter-offer-popup')) {
  customElements.define('newsletter-offer-popup', class extends HTMLElement {
    static memory = new Map();

    static storage(kind, key, value) {
      const memoryKey = `${kind}:${key}`;
      try {
        const store = window[kind];
        if (value === undefined) {
          const raw = store.getItem(key);
          return raw === null ? null : JSON.parse(raw);
        }
        if (value === null) store.removeItem(key);
        else store.setItem(key, JSON.stringify(value));
      } catch (_) { /* Storage can be unavailable in private browsing. */ }
      if (value !== undefined) {
        if (value === null) this.memory.delete(memoryKey);
        else this.memory.set(memoryKey, value);
      }
      return this.memory.get(memoryKey) ?? null;
    }

    static normalizeState(value, now = Date.now()) {
      const state = value && typeof value === 'object' && !Array.isArray(value) ? value : {};
      const confirmed = state.version === 2 && state.subscribed === true &&
        Number.isFinite(state.confirmedAt) && state.confirmedAt > 0 && state.confirmedAt <= now;
      const clean = { version: 2 };
      // Legacy success flags have no confirmed form response behind them. Let
      // those browsers see the signup again, including with once-only frequency.
      if ((!state.subscribed || confirmed) && Number.isFinite(state.lastShown) && state.lastShown > 0 && state.lastShown <= now) {
        clean.lastShown = state.lastShown;
      }
      if (confirmed) {
        clean.subscribed = true;
        clean.confirmedAt = state.confirmedAt;
      }
      return clean;
    }

    static visitor() {
      let firstVisit = this.storage('sessionStorage', 'aevart-offer-first-visit');
      if (firstVisit === null) {
        firstVisit = !this.storage('localStorage', 'aevart-offer-visited');
        this.storage('sessionStorage', 'aevart-offer-first-visit', firstVisit);
        this.storage('localStorage', 'aevart-offer-visited', true);
      }
      return { firstVisit };
    }

    static eligible(config, context) {
      const audience = config.audience || 'all';
      if (audience === 'new' && !context.firstVisit) return false;
      if (audience === 'returning' && context.firstVisit) return false;
      if (audience === 'guests' && context.customer) return false;
      if (audience === 'customers' && !context.customer) return false;
      if (audience === 'not_subscribed' && context.subscribed) return false;
      if (config.device === 'desktop' && context.mobile) return false;
      if (config.device === 'mobile' && !context.mobile) return false;

      const scope = config.pageScope || 'all';
      const pageTypes = { home: 'index', products: 'product', collections: 'collection' };
      if (pageTypes[scope] && context.pageType !== pageTypes[scope]) return false;
      if (scope === 'include' || scope === 'exclude') {
        const paths = String(config.paths || '').split(/[\n,]+/).map(path => path.trim()).filter(Boolean);
        const matches = paths.some(path => {
          if (config.pathMatch === 'exact') return context.path === path;
          if (config.pathMatch === 'starts_with') return context.path.startsWith(path);
          return context.path.includes(path);
        });
        if ((scope === 'include' && !matches) || (scope === 'exclude' && matches)) return false;
      }
      if (config.countryScope && config.countryScope !== 'all') {
        const countries = String(config.countries || '').toUpperCase().split(/[\s,;]+/).filter(Boolean);
        const matches = countries.includes(String(context.country || '').toUpperCase());
        if ((config.countryScope === 'include' && !matches) || (config.countryScope === 'exclude' && matches)) return false;
      }
      if (config.dateEnabled) {
        const validDate = value => {
          if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
          const date = new Date(`${value}T12:00:00Z`);
          return Number.isFinite(date.valueOf()) && date.toISOString().slice(0, 10) === value;
        };
        const start = String(config.startDate || '').trim();
        const end = String(config.endDate || '').trim();
        if ((start && !validDate(start)) || (end && !validDate(end))) return false;
        if (start && end && start > end) return false;
        if ((start && context.today < start) || (end && context.today > end)) return false;
      }
      return true;
    }

    static frequencyAllows(config, state, shownThisSession, now = Date.now()) {
      if (state.subscribed === true) return false;
      if (config.frequency === 'session') return !shownThisSession;
      if (config.frequency === 'every_page') return true;
      if (config.frequency === 'interval') {
        const units = { hours: 3600000, days: 86400000, weeks: 604800000 };
        const interval = Math.max(1, Number(config.repeatCount) || 1) * (units[config.repeatUnit] || units.days);
        return !state.lastShown || now - state.lastShown >= interval;
      }
      return !state.lastShown;
    }

    connectedCallback() {
      if (this.events) return;
      this.config = JSON.parse(this.querySelector('[data-offer-config]').textContent);
      this.dialog = this.querySelector('dialog');
      this.form = this.querySelector('form');
      this.teaser = this.querySelector('[data-offer-teaser]');
      // Keep an untouched template in the section. Moving its live clone into the
      // header must not remove the reminder from the editor's section snapshots.
      this.bar = this.bar || this.querySelector('[data-offer-bar-template]')?.content.firstElementChild.cloneNode(true) || this.querySelector('[data-offer-bar]');
      this.designMode = this.dataset.designMode === 'true';
      this.successPreview = this.designMode && this.dataset.previewStep === 'success';
      this.key = `aevart-offer:${this.config.campaign || this.dataset.sectionId}`;
      // Editor previews must neither inherit nor change a visitor's campaign.
      const savedState = this.designMode ? null : this.constructor.storage('localStorage', this.key);
      this.state = this.constructor.normalizeState(savedState);
      if (savedState && JSON.stringify(savedState) !== JSON.stringify(this.state)) {
        this.constructor.storage('localStorage', this.key, this.state);
      }
      this.visitor = this.designMode ? { firstVisit: true } : this.constructor.visitor();
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      this.device = window.matchMedia('(max-width: 767px)');
      this.autoRequested = false;
      this.autoOpened = false;
      this.barDismissed = this.designMode ? false : this.constructor.storage('sessionStorage', `${this.key}:bar-dismissed`);
      this.originalHeading = this.querySelector('[data-offer-signup] .offer-popup__heading')?.id || this.dialog.getAttribute('aria-labelledby');
      this.resolveLogo();
      this.renderStep(this.successPreview || this.state.subscribed === true);

      const handleClick = event => {
        if (event.target.closest('[data-offer-close]')) this.dialog.close();
        if (event.target.closest('[data-offer-teaser]')) this.open({ manual: true });
        if (event.target.closest('[data-offer-copy]')) this.copyCode();
        if (event.target.closest('[data-offer-bar-close]')) {
          this.bar.hidden = true;
          this.barDismissed = true;
          if (!this.designMode) this.constructor.storage('sessionStorage', `${this.key}:bar-dismissed`, true);
        }
      };
      this.addEventListener('click', handleClick, options);
      // The bar is inserted inside the theme header so its height is part of the header layout.
      this.bar.addEventListener('click', handleClick, options);
      this.dialog.addEventListener('click', event => {
        if (event.target !== this.dialog) return;
        const box = this.dialog.getBoundingClientRect();
        if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) this.dialog.close();
      }, options);
      this.dialog.addEventListener('close', () => {
        if (this.dialog.open) return;
        this.hasClosed = true;
        this.teaser.setAttribute('aria-expanded', 'false');
        this.updateEntrances();
      }, options);
      this.device.addEventListener('change', () => {
        if (!this.isEligible() && !this.designMode) this.dialog.close();
        this.updateEntrances();
      }, options);

      document.addEventListener('click', event => {
        const anchor = event.target.closest('a[href]');
        if (!anchor) return;
        const url = new URL(anchor.href, location.href);
        if (url.origin !== location.origin || url.pathname !== location.pathname || (!this.isOpenHash(url.hash) && !this.isResetHash(url.hash))) return;
        event.preventDefault();
        if (this.isResetHash(url.hash)) this.resetBrowserState();
        else this.open({ manual: true });
      }, options);
      window.addEventListener('hashchange', () => {
        if (this.isResetHash(location.hash)) this.resetBrowserState();
        else if (this.isOpenHash(location.hash)) this.open({ manual: true });
      }, options);
      window.addEventListener('storage', event => {
        if (this.designMode || event.key !== this.key) return;
        this.state = this.constructor.normalizeState(this.constructor.storage('localStorage', this.key));
        this.barDismissed = this.constructor.storage('sessionStorage', `${this.key}:bar-dismissed`);
        this.renderStep(this.state.subscribed === true);
        this.updateEntrances();
      }, options);

      if (this.form) {
        this.form.noValidate = true;
        this.form.addEventListener('submit', event => this.validateForm(event), { ...options, capture: true });
        this.form.addEventListener('input', event => event.target.removeAttribute('aria-invalid'), options);
      }
      const sectionSelected = event => {
        const wrapper = this.closest('.shopify-section');
        if (event.detail?.sectionId !== this.dataset.sectionId && !wrapper?.contains(event.target)) return;
        this.open({ preview: true, manual: true });
      };
      document.addEventListener('shopify:section:select', sectionSelected, options);
      document.addEventListener('shopify:block:select', sectionSelected, options);
      document.addEventListener('shopify:section:deselect', event => {
        if (event.detail?.sectionId === this.dataset.sectionId) this.dialog.close();
      }, options);

      const result = this.querySelector('[data-offer-result]')?.dataset.offerResult;
      const pending = this.designMode ? null : this.constructor.storage('sessionStorage', `${this.key}:pending`);
      const currentResponse = location.hash.split('?')[0] === `#${this.form?.id}` || new URL(location.href).searchParams.get('newsletter_offer') === this.dataset.sectionId;
      const elapsed = Date.now() - pending;
      const submittedHere = Number.isFinite(pending) && pending > 0 && elapsed >= 0 && elapsed < 600000;
      if (!this.designMode && this.isResetHash(location.hash)) {
        this.resetBrowserState();
      } else if (!this.designMode && (result === 'success' || result === 'error') && currentResponse && submittedHere) {
        this.constructor.storage('sessionStorage', `${this.key}:pending`, null);
        if (result === 'success') this.confirmSubscription();
        else {
          const summary = document.createElement('p');
          summary.textContent = this.config.submitError;
          this.querySelector('[data-offer-error]').prepend(summary);
        }
        this.clearResponseUrl();
        this.open({ manual: true, response: true });
      } else if (this.isOpenHash(location.hash)) {
        this.open({ manual: true });
      } else if (!this.designMode && this.dataset.enabled === 'true') {
        this.setupAutomaticTrigger(options);
      }
      this.updateEntrances();
    }

    isOpenHash(hash) {
      // The theme can append a variant query to an existing product-page hash.
      const anchor = hash.split('?')[0];
      return anchor === '#newsletter-popup-open' || anchor === '#newsletter-offer-open';
    }

    isResetHash(hash) {
      return hash.split('?')[0] === '#newsletter-offer-reset';
    }

    clearResponseUrl() {
      const url = new URL(location.href);
      if (url.searchParams.get('newsletter_offer') === this.dataset.sectionId) url.searchParams.delete('newsletter_offer');
      url.searchParams.delete('customer_posted');
      if (url.hash.split('?')[0] === `#${this.form?.id}` || this.isResetHash(url.hash)) url.hash = '';
      window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
    }

    resetBrowserState() {
      if (this.designMode) return;
      this.stopSubmissionWatch();
      clearTimeout(this.timer);
      clearTimeout(this.retryTimer);
      this.constructor.storage('localStorage', this.key, null);
      for (const suffix of ['shown', 'pending', 'bar-dismissed']) {
        this.constructor.storage('sessionStorage', `${this.key}:${suffix}`, null);
      }
      this.state = this.constructor.normalizeState(null);
      this.barDismissed = false;
      this.hasClosed = false;
      this.autoRequested = false;
      this.autoOpened = false;
      this.bar.hidden = true;
      this.renderStep(false);
      this.form?.reset();
      const error = this.querySelector('[data-offer-error]');
      if (error) {
        error.replaceChildren();
        error.hidden = true;
      }
      this.clearResponseUrl();
      this.updateEntrances();
      this.open({ manual: true });
    }

    isEligible({ allowSubscribed = false } = {}) {
      if (this.dataset.enabled !== 'true') return false;
      if (/^\/(challenge|checkouts?)(\/|$)/.test(location.pathname)) return false;
      return this.constructor.eligible(this.config, {
        ...this.visitor,
        customer: this.dataset.customer === 'true',
        subscribed: !allowSubscribed && (this.dataset.marketing === 'true' || this.state.subscribed),
        mobile: this.device.matches,
        path: location.pathname,
        pageType: this.config.pageType,
        country: this.dataset.country,
        today: this.dataset.shopDate
      });
    }

    setupAutomaticTrigger(options) {
      if (!this.isEligible()) return;
      const trigger = this.config.trigger || 'delay';
      if (trigger === 'manual') return;
      const requestOpen = () => {
        this.autoRequested = true;
        this.tryAutomaticOpen();
      };
      document.addEventListener('visibilitychange', () => {
        if (this.autoRequested) this.tryAutomaticOpen();
      }, options);
      if (trigger === 'scroll') {
        const onScroll = () => {
          const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
          const percentage = maxScroll > 0 ? window.scrollY / maxScroll * 100 : 0;
          if (percentage >= Number(this.config.scroll || 0)) requestOpen();
        };
        window.addEventListener('scroll', onScroll, { ...options, passive: true });
        onScroll();
      } else if (trigger === 'exit' && !this.device.matches) {
        document.addEventListener('mouseout', event => {
          if (!event.relatedTarget && event.clientY <= 0) requestOpen();
        }, options);
      } else {
        this.timer = window.setTimeout(requestOpen, Math.max(0, Number(this.config.delay) || 0) * 1000);
      }
    }

    tryAutomaticOpen() {
      if (!this.isConnected || this.autoOpened || document.visibilityState === 'hidden') return;
      const sessionShown = this.constructor.storage('sessionStorage', `${this.key}:shown`);
      if (!this.constructor.frequencyAllows(this.config, this.state, sessionShown) || !this.isEligible()) return;
      if (document.querySelector('dialog[open], .popup.is-active') || window.themeCore?.ageCheckPopupOpen) {
        clearTimeout(this.retryTimer);
        this.retryTimer = window.setTimeout(() => this.tryAutomaticOpen(), 1500);
        return;
      }
      this.autoOpened = this.open();
    }

    open({ preview = false, manual = false, response = false } = {}) {
      // A native modal occupies the browser's top layer. Do not cover a CAPTCHA
      // challenge that Shopify renders outside this dialog.
      if (this.hasActiveCaptchaChallenge()) return false;
      const editorPreview = this.designMode && (preview || manual);
      if (!editorPreview && !response && !this.isEligible()) return false;
      this.renderStep(this.designMode ? this.successPreview : this.state.subscribed === true);
      if (this.dialog.open) return true;
      if (!manual && document.querySelector('dialog[open]')) return false;
      this.dialog.showModal();
      this.teaser.hidden = true;
      this.teaser.setAttribute('aria-expanded', 'true');
      if (!editorPreview) {
        this.state.lastShown = Date.now();
        this.constructor.storage('localStorage', this.key, this.state);
        this.constructor.storage('sessionStorage', `${this.key}:shown`, true);
      }
      return true;
    }

    hasActiveCaptchaChallenge() {
      const frames = document.querySelectorAll('iframe[src*="hcaptcha.com"], iframe[title="hCaptcha challenge"], iframe[title="Main content of the hCaptcha challenge"]');
      return [...frames].some(frame => {
        const source = frame.getAttribute('src') || '';
        const isChallenge = source.includes('frame=challenge') || /^(?:main content of the )?hcaptcha challenge$/i.test(frame.title || '');
        return isChallenge && frame.getClientRects().length > 0 && getComputedStyle(frame).visibility !== 'hidden';
      });
    }

    validateForm(event) {
      const error = this.querySelector('[data-offer-error]');
      const fields = [...this.form.querySelectorAll('[data-offer-field]')];
      let invalid;
      let message;
      fields.forEach(field => {
        field.removeAttribute('aria-invalid');
        const kind = field.dataset.offerField;
        let valid = true;
        if (kind === 'name') valid = field.value.trim().length > 0;
        if (kind === 'email') valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(field.value.trim()) && field.validity.valid;
        if (kind === 'phone') valid = /^\+?[\d\s().-]+$/.test(field.value.trim()) && field.value.replace(/\D/g, '').length >= 7 && field.value.replace(/\D/g, '').length <= 15;
        if (kind === 'consent') valid = field.checked;
        if (!valid) {
          field.setAttribute('aria-invalid', 'true');
          if (!invalid) {
            invalid = field;
            message = this.config[`${kind}Error`];
          }
        }
      });
      if (invalid || this.designMode) {
        event.preventDefault();
        event.stopImmediatePropagation();
        error.textContent = invalid ? message : 'Open the storefront preview to test newsletter subscriptions.';
        error.hidden = false;
        invalid?.focus();
        return;
      }
      error.hidden = true;
      this.constructor.storage('sessionStorage', `${this.key}:pending`, Date.now());
      this.watchSubmission();
    }

    stopSubmissionWatch() {
      clearTimeout(this.submissionTimer);
      this.form?.removeAttribute('aria-busy');
      const restoreModal = this.dialog?.open && this.dialog.getAttribute('data-offer-submitting') === 'true';
      this.dialog?.removeAttribute('data-offer-submitting');
      if (restoreModal && this.isConnected) {
        this.dialog.close();
        this.dialog.showModal();
      }
      const status = this.querySelector('[data-offer-submit-status]');
      if (status) status.hidden = true;
    }

    watchSubmission() {
      this.stopSubmissionWatch();
      this.submissionStarted = Date.now();
      this.submissionDialogReleased = false;
      this.form.setAttribute('aria-busy', 'true');
      const status = this.querySelector('[data-offer-submit-status]');
      if (status) status.hidden = false;
      if (this.dialog.open) {
        this.dialog.setAttribute('data-offer-submitting', 'true');
        this.dialog.close();
        this.dialog.show();
      }
      const check = () => {
        if (!this.isConnected) return this.stopSubmissionWatch();
        if (this.hasActiveCaptchaChallenge()) {
          this.submissionStarted = Date.now();
          if (this.dialog.open) {
            this.submissionDialogReleased = true;
            this.dialog.close();
          }
        } else if (Date.now() - this.submissionStarted >= 60000) {
          this.stopSubmissionWatch();
          const error = this.querySelector('[data-offer-error]');
          error.textContent = this.config.submitTimeout || this.config.submitError;
          error.hidden = false;
          if (this.submissionDialogReleased) this.open({ manual: true, response: true });
          return;
        }
        this.submissionTimer = window.setTimeout(check, 200);
      };
      this.submissionTimer = window.setTimeout(check, 0);
    }

    confirmSubscription() {
      if (this.designMode) return;
      this.state.version = 2;
      this.state.subscribed = true;
      this.state.confirmedAt = Date.now();
      this.state.lastShown = this.state.confirmedAt;
      this.constructor.storage('localStorage', this.key, this.state);
      this.renderStep(true);
      this.updateEntrances();
    }

    renderStep(success) {
      this.querySelector('[data-offer-signup]').hidden = success;
      this.querySelector('[data-offer-success]').hidden = !success;
      if (!success) {
        this.dialog.setAttribute('aria-labelledby', this.originalHeading);
        return;
      }
      const heading = this.querySelector('[data-offer-success-heading]');
      heading.id = `${this.id}-success-heading`;
      this.dialog.setAttribute('aria-labelledby', heading.id);
      this.querySelector('[data-offer-success-text]').textContent = String(this.config.successText || '').replaceAll('{{reward_label}}', this.config.code || '');
    }

    updateEntrances() {
      const allowed = this.designMode || this.isEligible({ allowSubscribed: this.state.subscribed });
      const canShowTeaser = this.designMode || this.hasClosed || this.state.lastShown || this.config.trigger === 'manual';
      this.teaser.hidden = !allowed || !canShowTeaser || !this.config.showTeaser || this.dialog.open;
      const showReward = this.designMode ? this.successPreview : this.state.subscribed === true;
      if (allowed && showReward && this.config.code && this.config.showBar && (!this.barDismissed || this.successPreview)) {
        // The editor or a header refresh can replace the header after the popup connects.
        if (this.bar.parentElement === this || !this.bar.isConnected) {
          this.bar.style.cssText = this.style.cssText;
          (document.querySelector('header.header') || document.body).prepend(this.bar);
        }
        this.bar.hidden = false;
      } else {
        this.bar.hidden = true;
      }
    }

    resolveLogo() {
      const brand = this.querySelector('[data-theme-logo]');
      const logo = document.querySelector('header .header__heading-logo:not(.header__heading-logo--transparent)');
      if (!brand || !logo) return;
      const image = logo.cloneNode(false);
      image.className = 'offer-popup__logo';
      image.removeAttribute('style');
      image.removeAttribute('id');
      image.alt = logo.alt || brand.textContent.trim();
      brand.replaceChildren(image);
    }

    async copyCode() {
      const code = this.config.code;
      if (!code) return;
      let message;
      try {
        await navigator.clipboard.writeText(code);
        message = this.config.copiedText;
      } catch (_) {
        message = `Your code: ${code}`;
        if (this.dialog.open) this.querySelector('[data-offer-code]')?.select();
      }
      this.querySelector('[data-offer-copy-status]')?.replaceChildren(document.createTextNode(message));
      this.bar.querySelector('[data-offer-bar-status]').textContent = message;
    }

    disconnectedCallback() {
      this.stopSubmissionWatch();
      clearTimeout(this.timer);
      clearTimeout(this.retryTimer);
      this.events?.abort();
      this.events = null;
      if (this.dialog?.open) this.dialog.close();
      if (this.bar && this.bar.parentElement !== this) this.bar.remove();
    }
  });
}
